import {
  StatsContainer,
  DataTable,
  Button,
  DropDownInput,
  InputField,
  DateInput,
} from "@/components";
import { RightWrapper } from "../../../components";
import { useMemo, useCallback, useState, useEffect } from "react"; // Added useState
import { getCodRemittanceData } from "../../../utils/Data";
import { HiOutlineDownload, HiOutlineUpload } from "react-icons/hi";
import ColumnHelper from "@/utils/ColumnHelper";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"; // Ensure Sheet components are properly imported
import CodRemittanceBulk from "./CodRemittanceBulk";
import { useFilteredData } from "@/";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCODRemittances,
  selectAllCODRemittances,
} from "../../../app/Slices";
import { subDays } from "date-fns";
import CustomerPaymentForm from "./CustomerPaymentForm";
import { RiFileExcel2Line } from "react-icons/ri";
import { useApi } from "@/";
const CodRemittance = () => {
  const { callApi, loading } = useApi();
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [customerSheet, setCustomerSheet] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // Initial filters
  const [filters, setFilters] = useState({
    customerName: "",
    customerId: "",
    remittanceId: "", // ✅ Keep the correct key
    paymentType: "", // ✅ Match filterConfig key
    utrReference: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);

  const dispatch = useDispatch();
  const fetchData = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchCODRemittances({
          startDate: filters.date.from,
          endDate: filters.date.to,
        })
      );

      if (fetchCODRemittances.fulfilled.match(response)) {
        // console.log("API Response:", response.payload);
      } else if (fetchCODRemittances.rejected.match(response)) {
        // console.error("API Error:", response.payload);
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  }, [dispatch, filters.date]);

  useEffect(() => {
    fetchData();
  }, [appliedFilters.date, dispatch]);

  const codRemittance = useSelector(selectAllCODRemittances) || [];
  const totalDueFromCourier = useSelector(
    (state) => state.codRemittance.totalDueFromCourier
  );
  const totalReceiveFromCourier = useSelector(
    (state) => state.codRemittance.totalReceiveFromCourier
  );
  const totalCodAmountGivenToCustomer = useSelector(
    (state) => state.codRemittance.totalCodAmountGivenToCustomer
  );
  const totalCodAmountDueToCustomer = useSelector(
    (state) => state.codRemittance.totalCodAmountDueToCustomer
  );
  const filterConfig = useMemo(
    () => ({
      customerName: { type: "partial", itemKey: "userId.name" },
      customerId: { type: "partial", itemKey: "userId.clientId" },
      remittanceId: { type: "partial", itemKey: "remittanceId" },
      paymentType: { type: "exact", itemKey: "paymentMethod" },
      utrReference: { type: "comma", itemKey: "transactionId" },
      date: { type: "date", itemKey: "createdAt" },
    }),
    []
  );

  const { data: filteredData, isLoading } = useFilteredData(
    codRemittance,
    appliedFilters,
    filterConfig
  );
  const handleDownload = async (remittanceId) => {
    console.log(remittanceId);
    setIsDownloading(true);
    try {
      const response = await callApi(
        "/common/export",
        "POST",
        {
          model: "User CODRemittance",
          ids: [remittanceId],
          filename: `Remittance-${remittanceId}-${new Date().getTime()}.csv`,
        },
        null,
        null,
        "blob"
      );

      // Check if response is JSON error
      const contentType = response?.headers?.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        console.error("Export failed:", errorData.message);
        return; // Exit early
      }

      // Proceed with blob handling if not JSON
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Remittance-${remittanceId}-${new Date().getTime()}.csv`;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBulk = () => {
    setSheetOpen(true); // Opens the sheet for bulk updates
  };
  const handleCustomerBulk = () => {
    setCustomerSheet(true); // Opens the sheet for bulk updates
  };
  const paymentTypeOptions = useMemo(() => {
    // ✅ Ensure the options are correctly formatted
    return [
      { label: "Wallet", value: "Wallet" },
      { label: "Bank Account", value: "Bank Account" },
    ];
  }, []);
  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "userId.name",
        meta: {
          tooltip: (row) => row.userId?.name,
        },
      }),
      ColumnHelper({
        header: "Customer Id",
        accessorKey: "userId.clientId",
        meta: {
          tooltip: (row) => row.userId?.clientId,
        },
      }),
      ColumnHelper({
        header: "Remittance",
        accessorKey: "remittanceId",
        meta: {
          tooltip: (row) => row.remittanceId,
        },
      }),
      ColumnHelper({
        header: "COD Amount",
        accessorKey: "totalPaidAmount",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalPaidAmount)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalPaidAmount),
        },
      }),
      ColumnHelper({
        header: "Payment Date",
        accessorKey: "createdAt",
        cell: (info) => formatDate(info.row.original.createdAt),
        meta: {
          tooltip: (row) => formatDate(row.createdAt),
        },
      }),
      ColumnHelper({
        header: "Wallet Payment",
        accessorKey: "",
        cell: (info) => (
          <span className="font-nunito">
            {info.row.original.paymentMethod === "Wallet"
              ? formatCurrency(info.row.original.totalPaidAmount)
              : "---"}
          </span>
        ),
        meta: {
          tooltip: (row) =>
            row.paymentMethod === "Wallet"
              ? formatCurrency(row.totalPaidAmount)
              : "---",
        },
      }),
      ColumnHelper({
        header: "Account Payment",
        accessorKey: "",
        cell: (info) => (
          <span className="font-nunito">
            {info.row.original.paymentMethod === "Bank Account"
              ? formatCurrency(info.row.original.totalPaidAmount)
              : "---"}
          </span>
        ),
        meta: {
          tooltip: (row) =>
            row.paymentMethod === "Bank Account"
              ? formatCurrency(row.totalPaidAmount)
              : "---",
        },
      }),
      ColumnHelper({
        header: "UTR/Reference",
        accessorKey: "transactionId",
        meta: {
          tooltip: (row) => row.transactionId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Download",
        accessorKey: "id",
        cell: (info) => (
          <div className="flex gap-1 justify-center items-center">
            <Button
              size="xs"
              icon={HiOutlineDownload}
              onClick={() => handleDownload(info.row.original._id)}
            ></Button>
          </div>
        ),
        meta: {
          tooltip: () => "Download remittance details",
        },
      }),
    ],
    []
  );
  const formatAmount = (num) => parseFloat(num).toFixed(2);

  const StatsCodDue = [
    {
      statusName: "Received from Courier Partner ",
      statusNumber: formatAmount(totalReceiveFromCourier) || 0,
    },
    {
      statusName: "Remittance given to Customers  ",
      statusNumber: formatAmount(totalCodAmountGivenToCustomer) || 0,
    },
    {
      statusName: "Remittance Due to Customers  ",
      statusNumber: formatAmount(totalCodAmountDueToCustomer) || 0,
    },
    {
      statusName: "Total Remittance Due From Courier Partner  ",
      statusNumber: formatAmount(totalDueFromCourier) || 0,
    },
  ];
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);
  const handleApply = useCallback(() => {
    console.log("Filters applied:", filters);
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  const handleReset = useCallback(() => {
    const initialFilters = {
      customerName: "",
      customerId: "",
      remittanceId: "", // ✅ Keep the correct key
      paymentType: "", // ✅ Match filterConfig key
      utrReference: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);

  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent aria-describedby="">
            <SheetHeader>
              <SheetTitle>Update Bulk Due</SheetTitle>
            </SheetHeader>
            <CodRemittanceBulk
              setSheetOpen={setSheetOpen} // Fixed empty function
            />
          </SheetContent>
        </Sheet>
        <Sheet open={customerSheet} onOpenChange={setCustomerSheet}>
          <SheetContent aria-describedby="">
            <SheetHeader>
              <SheetTitle>Upload Customer Due</SheetTitle>
            </SheetHeader>
            <CustomerPaymentForm
              setSheetOpen={setCustomerSheet} // Fixed empty function
            />
          </SheetContent>
        </Sheet>
        {/* Filters Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <DateInput
            label="Date Filter"
            placeholder="Select From & To Date"
            mode="range"
            value={filters.date}
            onChange={handleDateChange}
          />
          <InputField
            label="Customer Name"
            placeholder="Enter Customer Name"
            value={filters.customerName} // 🔄 Changed from filters.awbsID to filters.awbId
            onChange={(value) => handleChange("customerName", value)} // 🔄 Changed from "awbsID" to "awbId
          />
          <InputField
            label="Customer ID"
            placeholder="Enter Customer ID"
            value={filters.customerId}
            onChange={(value) => handleChange("customerId", value)}
          />
          <InputField
            label="Remittance ID"
            placeholder="Enter Remittance ID"
            value={filters.remittanceId}
            onChange={(value) => handleChange("remittanceId", value)}
          />
          <DropDownInput
            label="Payment Type"
            placeholder="Select Payment Type"
            options={paymentTypeOptions}
            value={filters.paymentType}
            onChange={(value) => handleChange("paymentType", value)}
          />

          <InputField
            label="UTR/Reference"
            placeholder="Enter UTR/Reference separated by comma"
            value={filters.utrReference} // 🔄 Changed from filters.awbsID to filters.awbId
            onChange={(value) => handleChange("utrReference", value)} // 🔄 Changed from "awbsID" to "awbId
          />
          <div className="flex flex-row gap-2 justify-start items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              className="h-12"
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
        </div>
        <StatsContainer
          status={StatsCodDue}
          label="COD Remittance "
          lgCols={4}
        />
        <DataTable
          columns={columns}
          tittle="COD Remittance"
          data={filteredData}
          resetPage={resetPageCounter}
          isLoading={isLoading}
          showCheckbox={false}
          actions={[
            {
              name: "Courier Payment Upload",
              onClick: handleBulk,
              icon: HiOutlineUpload,
            },
            {
              name: "Customer COD Payment Upload",
              onClick: handleCustomerBulk,
              icon: HiOutlineUpload,
            },
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin: Export CODRemittance",
                filename: `COD_Remittance_${new Date().getTime()}`,
              },
            },
          ]}
        />
      </div>
    </RightWrapper>
  );
};

export default CodRemittance;
